import { Box, Tab, Tabs } from '@mui/material'
import { getOrgUser } from 'api/users'
import PageTitle from 'components/page/PageTitle'
import LoadingPlaceholder from 'components/placeholders/LoadingPlaceholder'
import { getTitle } from 'components/tabs/TabsTitle'
import React, { SyntheticEvent, useCallback, useMemo, useState } from 'react'
import { useQuery } from 'react-query'
import { useNavigate, useParams } from 'react-router-dom'
import { routes } from 'routes/routes'
import { UserOrgTab, UserTypeTab } from 'types'
import { getRoute } from 'utils'
import { UserDetailsBreadcrumbs } from '../UserDetailsBreadcrumbs/UserDetailsBreadcrumbs'
import { UserHistory } from '../UserHistory/UserHistory'
import { UserNotifications } from '../UserNotifications/UserNotifications'
import { UserOrgDetailsForm } from '../UserOrgDetailsForm/UserOrgDetailsForm'
import { UserOrgRoles } from '../UserOrgRoles/UserOrgRoles'

export function UserOrgDetails({ userId, isAdmin }: { userId: number; isAdmin: boolean }) {
  const navigate = useNavigate()
  const { tab } = useParams()

  const selectedTab = useMemo(() => {
    if (!tab) return UserOrgTab.Details

    return tab as UserOrgTab
  }, [tab])

  const userQuery = useQuery(['user', userId], () => getOrgUser({ id: userId }))

  const handleAfterUpdate = useCallback(() => {
    userQuery.refetch()
  }, [userQuery])

  const [needUpdateHistory, setNeedUpdateHistory] = useState(false)

  const handleResetNeedUpdateHistory = useCallback(() => {
    setNeedUpdateHistory(false)
  }, [])

  const handleChangeSelectedTab = useCallback(
    (_: SyntheticEvent, newValue: UserOrgTab) => {
      const routeName = isAdmin ? routes.adminOrgUserDetails : routes.orgUserDetails
      const route = getRoute(routeName, { userId, tab: newValue })
      navigate(route)
    },
    [isAdmin, navigate, userId],
  )

  const usersRoute = useMemo(() => {
    if (isAdmin) {
      return getRoute(routes.users, { userType: UserTypeTab.ORG })
    }

    return getRoute(routes.orgUsers, { userType: UserTypeTab.ORG })
  }, [isAdmin])

  if (userQuery.isFetching) {
    return <LoadingPlaceholder />
  }

  if (!userQuery.data) {
    return <div>Something went wrong</div>
  }

  return (
    <>
      <PageTitle>Edit User View</PageTitle>
      <UserDetailsBreadcrumbs user={userQuery.data.data} route={usersRoute} />

      <Box mt={2}>
        <Tabs value={selectedTab} onChange={handleChangeSelectedTab} indicatorColor="primary">
          <Tab value={UserOrgTab.Details} label={getTitle('Edit Profile')} />
          <Tab value={UserOrgTab.Roles} label={getTitle('Edit Roles')} />
          <Tab value={UserOrgTab.Notifications} label={getTitle('Notifications')} />
        </Tabs>
      </Box>

      {selectedTab === UserOrgTab.Details && (
        <>
          <UserOrgDetailsForm
            user={userQuery.data.data}
            isAdminPage={isAdmin}
            onAfterUpdate={handleAfterUpdate}
          />

          <UserHistory
            userId={userId}
            needUpdate={needUpdateHistory}
            resetNeedUpdate={handleResetNeedUpdateHistory}
          />
        </>
      )}

      {selectedTab === UserOrgTab.Roles && <UserOrgRoles user={userQuery.data.data} />}

      {selectedTab === UserOrgTab.Notifications && (
        <UserNotifications userId={userQuery.data.data.id} />
      )}
    </>
  )
}
